import { useRefHistory } from '@vueuse/core'
import { computed, readonly, ref, watch } from 'vue'
import logger from '../../logger'
import { MessageBuilder } from './backendMessageBuilder'
import { createInputTypeCommand, type Commands } from './commandBuilder'
import { createConversationInfo } from './messageBuilder'

import { extractJWTTokenFromMessage, getJWTToken, hasJWTToken, parseJWTToken } from './token'
import { uuidv4 } from '@/common/utils'
import signals from '../../common/signals'
import transport from './transport'
import { parseAgentData, parseInputType } from './helper'
import { useConversation } from './conversation'
import { isBoolean } from '@/common/utils'
import type { Botconfig, ChatInputCapabilities, ChatInputType, Intent, Operator } from '@/types'
import type { WSBotMessage, WSCommand, WSFileuploadCommand, WSHistoryMessage } from '@/types/WSBotMessage'



let isInitialized = false

const connectionStatus = ref('offline')
const isConnected = ref(false)
const botId = ref<string>()
const channelId = ref<string>()
const tokenLifetime = ref<number>()
const primusUrl = ref<string>()
const isTyping = ref(false)
const quickReplies = ref<Intent[]>([])

const inputType = ref<ChatInputType>({ type: 'textinput' })
const { undo } = useRefHistory(inputType)

const livechat = ref(false)
// init with empty operator to avoid errors
const defaultOperator = ref<Operator>(parseAgentData({
  name: '',
  title: '',
  avatar_url: ''
}))
const operator = ref<Operator>(parseAgentData({
  name: '',
  title: '',
  avatar_url: ''
}));
const currentRating = ref(0)
const showRating = ref(false)
/* const conversationRating = ref<ConversationRatingState>({
  prompt: false,
  current: 0,
}) */

const processCommands = (commands: Commands[]) => {
  // console.warn('command', commands.map((cmd) => cmd.cmd).join(', '))
  commands.forEach((msg: Commands) => {
    switch (msg.cmd) {
      case 'typing':
        setIsTyping(msg.value as boolean);
        break
      case 'qr':
        quickReplies.value = msg.value as Intent[]
        break
      case 'setInputType':
        inputType.value = msg.value as ChatInputType
        break
      case 'rate_conversation':
        // Do not show rating prompt once we have a rating already.
        if (currentRating.value !== 0) break;
        showRating.value = true;
        break;
      default:
        logger.error('error - command not known', msg)
    }
  })
}


const handleBotcommands = (data: WSCommand) => {
  const { command, payload } = data
  logger.log('bot command ->', command, payload)
  switch (data.command) {
    case 'init_livechat_takeover':
      if (livechat.value) logger.error('There is already a livechat session open')
      livechat.value = true
      break

    case 'start_livechat_takeover':
      if (!livechat.value) logger.error('livechat is not initialized')
      addConversationInfo('Livechat')
      break

    case 'stop_livechat_takeover':
      if (!livechat.value) return
      operator.value = defaultOperator.value
      addConversationInfo('Chatbot')
      sendIntent({
        text: undefined,
        intent: {
          name: 'livechat_ended',
          parameters: {confidence: 1}
        }
      }, true)
      livechat.value = false
      break

    case 'send_context':
      logger.error('send_context cmd is deprecated')
      break

    case 'agent_data':
      operator.value = parseAgentData(payload as Operator)
      break

    case 'set_input_capability':
      if (payload && 'capabilities' in payload) {
        if ((payload.capabilities as ChatInputCapabilities)?.fileupload?.enabled && (payload.capabilities as ChatInputCapabilities).fileupload.options) {
          const currentSize = (payload.capabilities as ChatInputCapabilities).fileupload.options?.maxFilesize;
          // Adjust to have proper Min Max values
          const maxFilesize = Number(currentSize) >= 1_000_000
            ? Number(currentSize)
            : 50_000_000; // Default 50 MB

          (payload.capabilities as ChatInputCapabilities).fileupload.options.maxFilesize = maxFilesize;
        }
        processCommands([createInputTypeCommand({
          type: 'textinput',
          capabilities: payload.capabilities as ChatInputCapabilities
        }, data.time)])
      }
      break

    case 'allow_file_upload':
      console.log('allow_file_upload', payload)
      processCommands([createInputTypeCommand({
        type: 'fileupload',
        options: {
          ...(payload as WSFileuploadCommand),
          // @ts-expect-error - this might be in the payload
          generateTokens: payload?.generateTokens || false
        }
      }, data.time)])
      break

      case 'custom_event':
        signals.customEvent('widget', { event: data.event, payload: data.payload || {} });
        break;

    case 'rate_conversation':
      // Do not show rating prompt once we have a rating already.
      if (currentRating.value !== 0) break;
      showRating.value = true;
      break;

    // see #239 - we do nothing here
    case 'timestamp':
      break
    default:
      logger.warn('no command operation found')
  }
}

const { conversation, processMessage, addOptimisticUpdate, handleLoopback, optimisticUpdates } =
  useConversation(processCommands)

const clearQuickReplies = () => (quickReplies.value = [])
const addConversationInfo = (message: string) => {
  const msg = createConversationInfo(message, {
    source: 'system', uuid: uuidv4(), operator: operator.value
  })
  conversation.value.set(msg.key, msg)
}

// ############################################################
// ### CONNECTION STATUS
// ############################################################

const connectionStatusHandler = (status: string) => {
  logger.log('connection status changed -> ', status)
  connectionStatus.value = status
}

// we need to send an timestamp command after a reconnect
watch(connectionStatus, (value, oldValue) => {
  if ((value === 'online' && oldValue === 'reconnect') || value === 'reconnected') {
    // @ts-expect-error - we can assume botId and channelId are set
    const message = new MessageBuilder(channelId.value)
      .setSender(getJWTToken())
      .setIsApiCall(true)
      .setCommand('timestamp')
      .build()

    logger.log('send reconnect')
    transport.send(message)
  }
})

// ############################################################
// ### init and setup
// ############################################################

function setConfig(config: Botconfig, primusUrlString: string) {
  if (!config) throw new Error('empty config')
  if (isInitialized) throw new Error('bot already initialized')

  botId.value = config.botId
  channelId.value = config.channelId
  tokenLifetime.value = config.tokenLifetime
  defaultOperator.value = config.components.widget.operator.default
  operator.value = config.components.widget.operator.default
  primusUrl.value = primusUrlString
  isInitialized = true
  parseJWTToken(config.botId, config.channelId, config.tokenLifetime)
}

async function connect(initialMessage?: Intent) {
  // to prevent a red flash on opening the widget we pretend to be online
  // if there will be an error, it will appear after the opening
  if (isConnected.value) {
    logger.warn('already connected')
    return
  }

  if (!primusUrl.value || !channelId.value) {
    logger.error('primusUrl or channelId not set')
    return
  }
  connectionStatus.value = 'online'
  await transport.connect({
    primusUrl: primusUrl.value,
    channelId: channelId.value,
    /* dataHandler: handleWSData, */
    connectionStatusHandler,
    onHistoryArray: handleHistoryData,
    onBotCommand: handleBotcommands,
    onBotMessage: handleSingleBotMessage,
    onUUIDLoopback: handleLoopback,
    onNewSession: onNewSession,
    handleMessageInject: handleMessageInject
  })
  isConnected.value = transport.isConnected()

  const message = new MessageBuilder(channelId.value)
    .setSender(getJWTToken())
    .isInitialMessage()
    .requestHistory()
    // addIntent ignores empty intent if there is no initial message given
    .addIntent(initialMessage)
    .build()
  transport.send(message)
  logger.log('connect to bot', message)
}

let initialHistoryParsed = false
const onNewSession = (data: WSBotMessage) => {
  logger.log('new session', data)
  // NOT SURE WHY WE DO THIS HERE
  // TODO CAN WE REMOVE THIS?
  initialHistoryParsed = true
  // @ts-expect-error - we can assume botId and channelId are set
  extractJWTTokenFromMessage(data, botId.value, channelId.value)
}

async function openConnection() {
  if (!isInitialized) throw new Error('bot not initialized')
  if (isConnected.value) return
  await connect()
}

// ############################################################

const handleSingleBotMessage = (data: WSBotMessage) => {
  // console.log('handleSingleBotMessage', data)
  processMessage(data, operator.value, livechat.value)
}

const handleMessageInject = (data: WSBotMessage) => {
  // console.log('handleSingleBotMessage', data)
  processMessage(data, operator.value, livechat.value)
}


/**
 * Check if the message is a file upload intent ('upload_success' or 'upload_cancel')
 * We don't filter that message because we need to set the input type as textinput
 *
 * Because otherwise on reload, the widget might have the wrong input type (fileupload) for example on Validate email step
 *
 * @param item
 * @returns {boolean}
 */
const isFileUploadIntentMessage = (item: WSHistoryMessage) => item.isFromClient && item.data.intent && ['upload_success', 'upload_cancel'].includes(item.data.intent.name)
const isEmptyMessage = (item: WSHistoryMessage) => item.isFromClient && !item.data.message
// ! TODO: Remove this logic when History data overwriting will be fixed. This done due to Fix a bug. When User during LiveChat opens FileUpload form AND in the same time Agent starts writing.
// ! As result, history (set_input_capability) overwrites FileInput form and user is kicked out from FileUpload process.
const isSetInputFileUpload = (item: WSHistoryMessage) => item?.command === 'set_input_capability' && 'capabilities' in item.payload && item.payload.capabilities?.fileupload && inputType.value.type === 'fileupload'
const hasQuickReplies = (data: WSBotMessage) => data?.quick_replies && data?.quick_replies?.length > 0
const handleHistoryData = (data: WSHistoryMessage[]) => {
  /**
   * Write the last set conversation rating before reversing the array.
   */
  const foundRating = data.find(it => it?.data?.intent?.name === 'set_conversation_rating' && it?.data?.intent?.parameters?.rating);
  if (foundRating) {
    currentRating.value = foundRating.data.intent!.parameters.rating as number;
  }

  // .reverse();
  // reverse to create the right order of executing for the messages
  // this is important because we need to set the propper Operator,
  // before parsing th message because the operator will be attached to
  // to message object

  data
    .filter((item) => (!isEmptyMessage(item) || isFileUploadIntentMessage(item)) && !isSetInputFileUpload(item)) // ! TODO: Remove after fixing History data overwriting
    .reverse()
    .forEach((item, index, cleanedMessages) => {
      // restore the operator from the history data and reexecute all commands
      // to make sure the operator is set correctly

      /* FileUpload during LiveChat Conversation. History.message may overwrite the inputType and attach file won't be available. We want to use it if available */
      // ! Probably the best way will be to Handle History Overwriting behavior, rather than provide fixes to each such case.
      const isInLiveChatAndCapabilities = getBotInfo().livechat && item.command === 'set_input_capability'; // WebChat Command that sets inputType

      if (item.api && (isInLiveChatAndCapabilities || !initialHistoryParsed)) {
        // cmds in the history have no time field
        handleBotcommands({ ...item, time: Date.now() } as WSCommand)
        return
      } else if (isFileUploadIntentMessage(item) && !livechat.value) { // Don't reset input type if in LiveChat (to be able to show FileUpload inout capabilities)
        return requestInputType();
      } else {
        const metafields = {
          uuid: item.uuid,
          messageFrom: item.messageFrom,
          isFromClient: item.isFromClient
        }

        const isFromAgent = item.messageFrom === 'agent' || item.isFromClient;
        const expandLinks = livechat.value && isFromAgent; // If in LiveChat && incoming array 'history' has a message.text with links, it will generate URL Buttons right after this text.

        processMessage(item?.data || item, operator.value, expandLinks, metafields)
        // this might not be the best solution
        if (
          !initialHistoryParsed &&
          cleanedMessages.length - 1 !== index &&
          !hasQuickReplies(item.data)
        ) {
          // If we don't have qr's attached to the last message
          // at the first restore of the history
          // we need to clear the quickreplies
          quickReplies.value = []
        }
      }
    })
  // we parsed the initial history, we can now set the flag to true
  initialHistoryParsed = true
}

// ############################################################
// ### Bot message processing and handling
// ############################################################

function sendInput(msg: string) {
  const tempId = uuidv4()
  // @ts-expect-error - we can assume botId and channelId are set
  const message = new MessageBuilder(channelId.value)
    .setSender(getJWTToken())
    .addMessage(msg)
    .addUUID(tempId)
    .build()

  addOptimisticUpdate({
    text: msg, tempId, timestamp: message.time
  });
  clearQuickReplies()

  logger.log('sending input', message)
  transport.send(message)
}

interface SendIntentOptions {
  /**
   * If true, we'll skip adding the text message to the chat history right away.
   * It'll be up to the API to include the text message in the history for it to appear.
   *
   * E.g. "Cancel" requires a text, but should not be shown in history.
   */
  skipOptimisticUpdate?: boolean;
}
async function sendIntent(intent: Intent, api: boolean = false, opts: SendIntentOptions = {}) {
  // if we are not connected yet, we assume this is an click on a teaser quick reply
  // we use the connected handler to send the intial message and make sure all fields are
  // propperly set like requestHistory and stuff.

  if (!transport.isConnected()) {
    await connect(intent)
    return
  }

  const tempId = uuidv4()
  // @ts-expect-error - we can assume botId and channelId are set
  const message = new MessageBuilder(channelId.value)
    .setSender(getJWTToken())
    .addIntent(intent)
    .setIsApiCall(api)
    .addUUID(tempId)
    .build()

  logger.log('sending intent', message)
  if (intent.text && !opts.skipOptimisticUpdate) {
    addOptimisticUpdate({
      text: intent.text, tempId, timestamp: message.time
    });
  }
  clearQuickReplies()
  transport.send(message)
}

const setConversationRating = (stars: number): void => {
  const message = new MessageBuilder(channelId.value!)
    .addIntent({
      intent: {
        name: 'set_conversation_rating',
        parameters: { rating: stars, confidence: 1 }
      },
    })
    .setSender(getJWTToken())
    .setIsApiCall(true)
    .build()
  transport.send(message)

  currentRating.value = stars
  showRating.value = false
};

const setShowConversationRating = (value: boolean): void => {
  showRating.value = value
}

// ############################################################
// ### Bot message processing and handling
// ############################################################

const requestInputType = (value: ChatInputType = { type: 'textinput' }) => {
  inputType.value = parseInputType(value);
};

const restorePreviousInputType = () => {
  undo();
};

const end = () => transport.end()
const disconnect = () => transport.disconnect()
const hasConnection = () => {

  return isConnected.value
}


const getBotInfo = () => {
  return {
    livechat: livechat.value,
    hasQR: quickReplies.value.length > 0,
    status: connectionStatus.value,
    isConnected: isConnected.value,
    isTyping: isTyping.value,
    isInitialized,
    botId: botId.value,
    channelId: channelId.value,
    tokenLifetime: tokenLifetime.value,
    primusUrl: primusUrl.value,
    operator: operator.value,
    inputType: inputType.value,
    optimisticUpdates: Array.from(optimisticUpdates.values()).length
  }
}
const getConversation = () => {
  return Array.from(conversation.value.values())
}

let typingTimeout: NodeJS.Timeout | null = null;
/**
 *
  * @param {boolean} value
 */
function setIsTyping(value: boolean) {
  if (!isBoolean(value)) return;
  isTyping.value = value;

  // For isTyping: true, we set a timeout to reset the typing indicator to prevent infinite typing.
  if (value) {
    if (typingTimeout) clearTimeout(typingTimeout);

    typingTimeout = setTimeout(() => {
      isTyping.value = false;
      typingTimeout = null;
    }, 30 * 1000);
  }
}

export function useMoinBot() {
  // current operator is the one that is currently talking to the user
  // it's defined by the bot
  // -> on livechat it's the operator that is currently talking to the user
  // if we don't have it, fall back to something neutral
  const getCurrentOperator = computed(() => {
    if (operator.value) return operator.value
    logger.warn('No operator found, using default operator')
    return {
      name: 'MoinBot',
      avatar_url: 'https://media.moin.ai/1595514610395_chatbot.png'
    }
  })

  // return the avatar url for the default bot set by the config
  // used for widget button ot the startscreen
  const getDefaultAvatarUrl = computed(() => defaultOperator?.value?.avatar_url)

  // expose managed state as return value
  return {
    botId,
    channelId,
    isConnected,
    connectionStatus,
    isTyping,
    showRating: readonly(showRating),
    currentRating: readonly(currentRating),
    quickReplies,
    hasQuickReplies: computed(() => quickReplies.value.length > 0),
    inputType,
    getInputType: inputType,
    clearQuickReplies,
    operator,
    getCurrentOperator,
    getDefaultAvatarUrl,
    conversation,
    setConfig,
    setIsTyping,
    //connect,
    openConnection,
    disconnect,
    end,
    sendTextInput: sendInput,
    sendInput,
    sendIntent,
    getBotInfo,
    getConversation,
    getJWTToken,
    hasJWTToken,
    hasConnection,
    addConversationInfo,
    defaultOperator,
    requestInputType,
    restorePreviousInputType,
    setConversationRating,
    setShowConversationRating
  }
}
